import { useCallback, useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { WarningBadgeIcon, CloseIcon } from "./icons"
import svg from "../../utils/svg"
import BubbleFeatureList from "../../components/BubbleFeatureList/BubbleFeatureList"
import ExpirationPaymentButtons from "../BubbleExpirationPaywall/ExpirationPaymentButtons/ExpirationPaymentButtons"
import LifetimeComingSoonPopup from "../BubbleExpirationPaywall/LifetimeComingSoonPopup/LifetimeComingSoonPopup"
import { PurchasProvider } from "../../context/purchaseContext"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import { useAppState } from "../../context/appStateContext"
import useStrings from "../../localization/useStrings"
import native from "../../native/native"
import styles from "./BackupPaywall.module.scss"

const BackupPaywall = () => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <BackupPaywallContent />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

const BackupPaywallContent = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { actions } = useAppState()
  const strings = useStrings().backupPaywall
  const [showLifetimePopup, setShowLifetimePopup] = useState(false)

  const handleLifetimeClick = useCallback(() => {
    setShowLifetimePopup(true)
  }, [])

  const handlePopupClose = useCallback(() => {
    setShowLifetimePopup(false)
  }, [])

  useEffect(() => {
    const isDark = document.documentElement.dataset.theme === "dark"
    const bgColor = isDark ? "#1a1e27" : "#f0f7ff"
    actions.setHeaderInsetColor(bgColor)
    actions.setIgnoreFooterInset(true)

    let el = containerRef.current?.parentElement
    while (el && el !== document.body) {
      el.style.backgroundColor = bgColor
      el = el.parentElement
    }
    document.body.style.backgroundColor = bgColor
    document.documentElement.style.backgroundColor = bgColor
  }, [actions])

  const handleClose = () => {
    native.postNativeEvent("close")
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <button onClick={handleClose} className={styles.closeButton}>
        <CloseIcon className={styles.closeIcon} />
      </button>
      <div className={styles.scrollArea}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={styles.headerSection}
        >
          <WarningBadgeIcon className={styles.headerImage} />
          <h1 className={styles.title}>{strings.title}</h1>
          <p className={styles.subtitle}>{strings.subtitle}</p>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <BubbleFeatureList />
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className={styles.ctaSection}
        >
          <h2 className={styles.premiumTitle}>
            <svg.PremiumIcon className={styles.premiumIcon} />
            {strings.subscribe}
          </h2>
          <ExpirationPaymentButtons onLifetimeClick={handleLifetimeClick} />
          <p className={styles.cancelText}>{strings.cancelAnytime}</p>
        </motion.div>
      </div>
      <LifetimeComingSoonPopup
        visible={showLifetimePopup}
        onClose={handlePopupClose}
      />
    </div>
  )
}

export default BackupPaywall
